import subpixel from '../util/canvasSubpixelOffset';
import LineTool from './LineTool';

/** Tool used to draw ellipse on chart object */
export default class EllipseLineTool extends LineTool {
	/**
	 * @param {ModchartsInteractive} core - Modcharts chart object
	 * @param {object} settings - Parameters for tool
	 */
	constructor(core, settings) {
		super(core, settings);

		/** @type {number} */
		this.INNER_TOLERANCE = 5;
		/** @type {number} */
		this.OUTER_TOLERANCE = 8;
	}

	/**
	 * Calculate data needed to create ellipse path
	 * @return {object} Data used to create ellipse path
	 */
	calcEllipseData() {
		const coords = [this.handle[0].getCoords(), this.handle[1].getCoords()];

		if (!coords[0] || !coords[1]) return {};

		const xDist = coords[1][0] - coords[0][0];
		const yDist = coords[1][1] - coords[0][1];
		const lineDist = this.distance(coords[0], coords[1]);
		const center = [coords[0][0] + xDist / 2, coords[0][1] + yDist / 2];

		return {
			center,
			coords,
			lineDist,
			xDist,
			yDist
		};
	}

	/**
	 * Draws tool control line
	 * @param {CanvasRenderingContext2D} ctx - HTML canvas context to be used for drawing graphics
	 * @param {Number[]} coords - Array containing coordinates for each end of the control line
	 */
	_drawControlLine(ctx, coords) {
		if (this.state.hover || this.state.selected) {
			ctx.save();
			ctx.strokeStyle = '#ccc';
			ctx.setLineDash([2, 3]);
			ctx.beginPath();
			ctx.moveTo(coords[0][0], coords[0][1]);
			ctx.lineTo(coords[1][0], coords[1][1]);
			ctx.stroke();
			ctx.restore();
		}
	}

	/**
	 * @return {object} Object containing default tool parameters
	 * @property {string} id - Tool ID
	 * @property {string} name - Tool name
	 * @property {object} style - Tool styles
	 */
	_getDefaultParams() {
		return {
			id: 'ellipse',
			name: 'Ellipse',
			style: this.core.getStyle('tool.ellipse')
		};
	}

	/**
	 * Draws inner and outer ellipses for donut shape
	 * @param {CanvasRenderingContext2D} ctx - HTML canvas context to be used for drawing graphics
	 * @param {number} cx - X coordinate of center of ellipse
	 * @param {number} cy - Y coordinate of center of ellipse
	 * @param {number} rx1 - Major axis radius of outer ellipse
	 * @param {number} ry1 - Minor axis radius of outer ellipse
	 * @param {number} rx2 - Major axis radius of inner ellipse
	 * @param {number} ry2 - Minor axis radius of inner ellipse
	 */
	_getDonutPath(ctx, cx, cy, rx1, ry1, rx2, ry2) {
		ctx.save();
		ctx.beginPath();
		// Draw outer ellipse
		ctx.ellipse(cx, cy, Math.abs(rx1), Math.abs(ry1), 0, 0, 2 * Math.PI, true);
		ctx.moveTo(cx + rx1, cy);
		// Draw inner ellipse
		ctx.ellipse(cx, cy, Math.abs(rx2), Math.abs(ry2), 0, 0, 2 * Math.PI, false);
		ctx.restore();
	}

	/**
	 * Creates two partial ellipses for use in creating filled arc shape
	 * @param {CanvasRenderingContext2D} ctx - HTML canvas context to be used for drawing graphics
	 * @param {number} cx - X coordinate of center of ellipse
	 * @param {number} cy - Y coordinate of center of ellipse
	 * @param {number} rx1 - Major axis radius of outer ellipse
	 * @param {number} ry1 - Minor axis radius of outer ellipse
	 * @param {number} rx2 - Major axis radius of inner ellipse
	 * @param {number} ry2 - Minor axis radius of inner ellipse
	 * @param {boolean} isSmiley - Whether or not arc is concave upward
	 */
	_getDonutSegmentPath(ctx, cx, cy, rx1, ry1, rx2, ry2, isSmiley) {
		let beginEllipse = Math.PI;
		let endEllipse = 0;

		// Change drawing direction if concave downward
		if (!isSmiley) {
			beginEllipse = 0;
			endEllipse = Math.PI;
		}

		ctx.save();
		ctx.beginPath();
		// Draw outer ellipse
		ctx.ellipse(cx, cy, Math.abs(rx1), Math.abs(ry1), 0, beginEllipse, endEllipse, true);
		ctx.moveTo(cx + rx1, cy);
		// Draw inner ellipse
		ctx.ellipse(cx, cy, Math.abs(rx2), Math.abs(ry2), 0, endEllipse, beginEllipse, false);
		ctx.restore();
	}

	/**
	 * Creates ellipse
	 * @param {CanvasRenderingContext2D} ctx - HTML canvas context to be used for drawing graphics
	 * @param {number} cx - X coordinate of center of ellipse
	 * @param {number} cy - Y coordinate of center of ellipse
	 * @param {number} rx - Major axis radius of ellipse
	 * @param {number} ry - Minor axis radius of ellipse
	 */
	_getEllipsePath(ctx, cx, cy, rx, ry) {
		ctx.save(); // save state
		ctx.beginPath();
		ctx.ellipse(cx, cy, Math.abs(rx), Math.abs(ry), 0, 0, 2 * Math.PI);
		ctx.restore(); // restore to original state
	}

	/**
	 * Creates half ellipse
	 * @param {CanvasRenderingContext2D} ctx - HTML canvas context to be used for drawing graphics
	 * @param {number} cx - X coordinate of center of ellipse
	 * @param {number} cy - Y coordinate of center of ellipse
	 * @param {number} rx - Major axis radius of ellipse
	 * @param {number} ry - Minor axis radius of ellipse
	 * @param {boolean} isSmiley - Whether or not arc is concave upward
	 */
	_getHalfArcPath(ctx, cx, cy, rx, ry, isSmiley) {
		ctx.save(); // save state
		ctx.beginPath();

		ctx.translate(cx - rx, cy - ry);
		ctx.scale(rx, ry);
		if (isSmiley) {
			ctx.arc(1, 1, 1, 0, Math.PI, false);
		} else {
			ctx.arc(1, 1, 1, Math.PI, 2 * Math.PI, false);
		}

		ctx.restore(); // restore to original state
	}

	/**
	 * @param {number[]} mousePanel - Array of mouse coordinates
	 */
	isWithin(mousePanel) {
		const {
			center,
			xDist,
			yDist
		} = this.calcEllipseData();

		// Fail if no ellipse data provided
		if (!center) return;

		const ctx = this.panel.rootToolContext;

		// within a slightly larger ellipse?
		this._getEllipsePath(
			ctx,
			center[0],
			center[1],
			Math.abs(xDist / 2) + this.OUTER_TOLERANCE,
			Math.abs(yDist / 2) + this.OUTER_TOLERANCE
		);
		const isWithinOuter = ctx.isPointInPath(mousePanel[0], mousePanel[1]);

		// within a slightly smaller ellipse?
		this._getEllipsePath(
			ctx,
			center[0],
			center[1],
			Math.abs(xDist / 2) - this.INNER_TOLERANCE,
			Math.abs(yDist / 2) - this.INNER_TOLERANCE
		);
		const isWithinInner = ctx.isPointInPath(mousePanel[0], mousePanel[1]);

		// has to be within outer but not inner
		return isWithinOuter && !isWithinInner;
	}

	onMousemove(mouse, touch, event) {

		const staticHandle = this.handle.find(h => h !== this.state.dragHandle);
		const staticCoords = staticHandle.getCoords();
		const newMouse = [...mouse];

		// Force drawing circle if dragging handle (i.e. not moving) and shift key is pressed
		if (
			this.state.dragHandle &&
			event &&
			event.shiftKey
		) {
			// Get X-distance between handles
			let xDiff = mouse[0] - staticCoords[0];
			const yDiff = mouse[1] - staticCoords[1];

			// Force Y-distance to match X-distance, account for
			if (
				(yDiff >= 0 && xDiff < 0) ||
					(yDiff < 0 && xDiff >= 0)
			) {
				// Flip sign if in lower left or upper right quadrants
				xDiff *= -1;
			}
			newMouse[1] = staticCoords[1] + xDiff;

			// Add panel top since it will be removed by parent method
			newMouse[1] += this.panel.size.top;
		}

		// Call parent method
		super.onMousemove(newMouse);
	}

	render() {
		const {
			center,
			coords,
			xDist,
			yDist
		} = this.calcEllipseData();

		// Fail if no ellipse data provided
		if (!center) return;

		const ctx = this.panel.rootToolContext;
		ctx.beginPath();

		// Set default styles
		ctx.strokeStyle = this.params.style.color;
		ctx.fillStyle = this.params.style.background.color;
		ctx.lineWidth = this.params.style.width;

		this._getEllipsePath(
			ctx,
			center[0],
			center[1],
			xDist / 2,
			yDist / 2
		);

		// Draw circle fills
		ctx.fill();

		// Draw circle lines (with no transparency)
		ctx.stroke();

		if (this.state.selected || this.state.hover) {
			this._renderBoundingBox(ctx, coords);
			this.handle[0].render();
			this.handle[1].render();
		}

		this.panel.core.onToolRender(this);
	}

	/**
	 * Draw dashed bounding box around tool
	 * @param {CanvasRenderingContext2D} ctx
	 * @param {number[]} coords
	 */
	_renderBoundingBox(ctx, coords) {
		ctx.save();
		ctx.strokeStyle = this.params.style.boundingBox.color;
		ctx.setLineDash(this.params.style.boundingBox.lineDash.split(/\s*,\s*/));
		ctx.globalAlpha = 0.5;
		ctx.beginPath();
		subpixel.rect(
			ctx,
			coords[0][0],
			coords[0][1],
			coords[1][0] - coords[0][0],
			coords[1][1] - coords[0][1]
		);
		ctx.stroke();
		ctx.restore();
	}
}
